import { Container } from 'unstated';
import * as voicifyApi from '../../api';
import IResult from '../../models/result/IResult';
import AppliedApplicationTemplateModel from '../../models/templating/api/AppliedApplicationTemplateModel';
import TemplateFormSectionModel from '../../models/templating/api/TemplateFormSectionModel';
import UpdateAppliedApplicationTemplateFormRequest from '../../models/templating/api/UpdateAppliedApplicationTemplateFormRequest';
import FinishTemplateSectionRequest from '../../models/templating/api/FinishTemplateSectionRequest';
import DynamicFormResponse from '../../models/templating/api/DynamicFormResponse';
import _ from 'lodash';
import FinalizeTemplateFormRequest from '../../models/templating/api/FinalizeTemplateFormRequest';
import TemplateFormFieldValueModel from '../../models/templating/api/TemplateFormFieldValueModel';
import UpdateAppliedApplicationTemplateFormsRequest from '../../models/templating/api/UpdateAppliedApplicationTemplateFormsRequest';
import { AutoGenerateAppliedAppValuesRequest } from '../../models/templating/api/AutoGenerateAppliedAppValuesRequest';
import { evaluateConditionalDefault } from '../../utils/conditionalDefaultUtils';

export interface PerApplicationAppliedTemplate {
    applicationId: string
    appliedTemplates: AppliedApplicationTemplateModel[]
}

export type MultipleTemplateSyncsStatus = "none" | "syncing" | "success" | "error" | "templateConfigDisabled";

type AppliedApplicationTemplateState = {
    appliedTemplates: AppliedApplicationTemplateModel[]
    perApplicationAppliedTemplates: PerApplicationAppliedTemplate[]
    isLoading: boolean
    isSaving: boolean
    isWaitingForTemplateSync: boolean
    errors: string[],
    templateSyncErrors: string[],
    appliedTemplateSyncId: string,
    multipleTemplateSyncsStatus: MultipleTemplateSyncsStatus,
}

export default class AppliedApplicationTemplateContainer extends Container<AppliedApplicationTemplateState> {
    // Track original values for change detection
    private originalValuesMap: Map<string, TemplateFormFieldValueModel[]> = new Map();

    // default state
    state = {
        appliedTemplates: [] as AppliedApplicationTemplateModel[],
        perApplicationAppliedTemplates: [] as PerApplicationAppliedTemplate[],
        isLoading: false,
        isSaving: false,
        isWaitingForTemplateSync: false,
        errors: [],
        templateSyncErrors: [],
        appliedTemplateSyncId: null,
        multipleTemplateSyncsStatus: "none" as MultipleTemplateSyncsStatus
    }

    loadAppliedTemplates(applicationId: string) {
        this.setLoading(true);
        const promise = voicifyApi.getAppliedApplicationTemplatesForApp(applicationId);
        promise.then(result => {
            if (result.resultType == "Ok") {
                const appliedForms = result.data;
                appliedForms.forEach(appliedForm => {
                    // find applied forms with dynamic form urls and create the values cache. 
                    if (appliedForm.templateForm.templateConfiguration.dynamicFormUrl) {
                        const allValues = appliedForm.templateFormFieldValues;
                        if (allValues && allValues.length > 0) {
                            const dynamicValues = allValues.filter(v => v.dynamicFormFieldId || v.dynamicFormFieldName) ?? [];
                            const standardValues = allValues.filter(v => v.templateFormFieldId) ?? [];
                            appliedForm.templateFormFieldValues = standardValues;
                            appliedForm.dynamicFormFieldValuesCache = dynamicValues; // values cache will be used when we add dynamic sections
                        }
                    }
                    // apply form field info to existing values
                    appliedForm.templateForm?.templateFormSections.forEach(section => {
                        section?.templateFormFields?.forEach(field => {
                            const value = appliedForm.templateFormFieldValues.find(v => v.templateFormFieldId == field.id);
                            if (value) {
                                value.fieldType = field.fieldType;
                                value.associatedVariable = field.associatedVariable;
                                value.label = field.label;
                                value.tip = field.tip;
                                value.title = field.title;
                                value.placeholder = field.placeholder;
                                value.defaultValue = field.defaultValue;
                            }
                        });
                    })

                    // Store original values for change detection
                    this.captureOriginalValues(appliedForm.id, appliedForm.templateFormFieldValues);
                });
                const applicationAppliedTemplates = { applicationId: applicationId, appliedTemplates: appliedForms };
                const newPerApp = this.state.perApplicationAppliedTemplates.filter(m => m.applicationId != applicationId);
                this.setState({
                    ...this.state,
                    isLoading: false,
                    appliedTemplates: appliedForms,
                    perApplicationAppliedTemplates: [
                        ...newPerApp,
                        applicationAppliedTemplates
                    ],
                    errors: []
                })
            } else {
                this.setState({
                    ...this.state,
                    isLoading: false,
                    errors: result.errors
                })
            }
        }).catch(e => {
            this.setState({
                ...this.state,
                isLoading: false,
                errors: [e?.toString()]
            })
        })
        return promise;
    }

    /**
     * Capture original values for change detection
     */
    private captureOriginalValues(appliedTemplateId: string, values: TemplateFormFieldValueModel[]) {
        this.originalValuesMap.set(appliedTemplateId, JSON.parse(JSON.stringify(values || [])));
    }

    /**
     * Get original values for a template
     */
    getOriginalValues(appliedTemplateId: string): TemplateFormFieldValueModel[] {
        return this.originalValuesMap.get(appliedTemplateId) || [];
    }

    /**
     * Get all original values for all templates (used by Spark)
     */
    getAllOriginalValues(): TemplateFormFieldValueModel[] {
        const allValues: TemplateFormFieldValueModel[] = [];
        this.originalValuesMap.forEach(values => {
            allValues.push(...values);
        });
        return allValues;
    }

    /**
     * Calculate changes in modal-friendly format
     * Returns array of changed fields with before/after values
     */
    getChangedFieldsForDisplay(appliedTemplateId: string, currentValues: TemplateFormFieldValueModel[]): Array<{
        fieldName: string;
        oldValue: string;
        newValue: string;
        state: "Added" | "Modified" | "Removed";
    }> {
        const originalValues = this.originalValuesMap.get(appliedTemplateId) || [];
        const changes: Array<{
            fieldName: string;
            oldValue: string;
            newValue: string;
            state: "Added" | "Modified" | "Removed";
        }> = [];

        // Check for modified and added fields
        currentValues?.forEach(currentField => {
            let originalField = null;

            // Try to find matching original field
            if (currentField.id) {
                originalField = originalValues.find(v => v.id === currentField.id);
            } else if (currentField.templateFormFieldId) {
                originalField = originalValues.find(v =>
                    v.templateFormFieldId === currentField.templateFormFieldId &&
                    v.appliedApplicationTemplateId === currentField.appliedApplicationTemplateId
                );
            } else if (currentField.dynamicFormFieldName) {
                originalField = originalValues.find(v => v.dynamicFormFieldName === currentField.dynamicFormFieldName);
            }

            const fieldName = currentField.title || currentField.label || currentField.dynamicFormFieldName || 'Untitled Field';

            if (!originalField) {
                // Field was added
                if (currentField.value && currentField.value.trim() !== '') {
                    changes.push({
                        fieldName,
                        oldValue: '',
                        newValue: currentField.value,
                        state: "Added"
                    });
                }
            } else if (originalField.value !== currentField.value) {
                // Field was modified
                changes.push({
                    fieldName,
                    oldValue: originalField.value || '',
                    newValue: currentField.value || '',
                    state: "Modified"
                });
            }
        });

        // Check for removed fields
        originalValues?.forEach(originalField => {
            let currentField = null;

            // Try to find matching current field
            if (originalField.id) {
                currentField = currentValues.find(v => v.id === originalField.id);
            } else if (originalField.templateFormFieldId) {
                currentField = currentValues.find(v =>
                    v.templateFormFieldId === originalField.templateFormFieldId &&
                    v.appliedApplicationTemplateId === originalField.appliedApplicationTemplateId
                );
            } else if (originalField.dynamicFormFieldName) {
                currentField = currentValues.find(v => v.dynamicFormFieldName === originalField.dynamicFormFieldName);
            }

            if (!currentField && originalField.value && originalField.value.trim() !== '') {
                const fieldName = originalField.title || originalField.label || originalField.dynamicFormFieldName || 'Untitled Field';
                changes.push({
                    fieldName,
                    oldValue: originalField.value,
                    newValue: '',
                    state: "Removed"
                });
            }
        });

        return changes;
    }

    /**
     * Calculate changes for multiple templates (Spark)
     */
    getAllChangedFieldsForDisplay(currentValuesByTemplate: Map<string, TemplateFormFieldValueModel[]>): Array<{
        fieldName: string;
        oldValue: string;
        newValue: string;
        state: "Added" | "Modified" | "Removed";
    }> {
        const allChanges: Array<{
            fieldName: string;
            oldValue: string;
            newValue: string;
            state: "Added" | "Modified" | "Removed";
        }> = [];

        currentValuesByTemplate.forEach((currentValues, templateId) => {
            const changes = this.getChangedFieldsForDisplay(templateId, currentValues);
            allChanges.push(...changes);
        });

        return allChanges;
    }

    /**
     * Compare the module schema (field definitions) against what the child app currently holds.
     * Returns schema-level changes — new fields, removed fields, and changed defaults — that
     * are not visible through the regular user-edit diff.
     *
     * isBlocker: true means a required new field has no defaultValue and no existing value;
     * the UI should block the Finish button until the user fills it in.
     */
    getSchemaChangesForDisplay(appliedTemplate: AppliedApplicationTemplateModel): Array<{
        fieldName: string;
        oldValue: string;
        newValue: string;
        state: "Added" | "Modified" | "Removed";
        isBlocker: boolean;
    }> {
        const changes: Array<{
            fieldName: string;
            oldValue: string;
            newValue: string;
            state: "Added" | "Modified" | "Removed";
            isBlocker: boolean;
        }> = [];

        const currentValues = appliedTemplate.templateFormFieldValues || [];
        const sections = appliedTemplate.templateForm?.templateFormSections || [];

        // Collect all schema field IDs for the "Removed" pass below
        const schemaFieldIds = new Set<string>();

        sections.forEach(section => {
            (section.templateFormFields || []).forEach(field => {
                if (!field.id) return;
                schemaFieldIds.add(field.id);

                const valueRecord = currentValues.find(v => v.templateFormFieldId === field.id);

                // Skip location-specific fields — they are managed per-location and should
                // never surface as schema changes on Shared Topics / Shared Configuration forms.
                // Location-specific is determined by the value record's sentinel pattern:
                // associatedVariable is present AND equals the stored value.
                if (valueRecord?.associatedVariable && valueRecord.associatedVariable === valueRecord.value) return;
                const fieldName = field.title || field.label || field.name || 'Untitled Field';

                if (!valueRecord) {
                    // Field exists in schema but child app has no value record at all
                    if (field.defaultValue != null && field.defaultValue !== '') {
                        // New field with a default — will be auto-populated on sync
                        changes.push({
                            fieldName,
                            oldValue: '',
                            newValue: field.defaultValue,
                            state: "Added",
                            isBlocker: false,
                        });
                    } else if (field.isRequired) {
                        // New required field with no default — blocks sync
                        changes.push({
                            fieldName,
                            oldValue: '',
                            newValue: '',
                            state: "Added",
                            isBlocker: true,
                        });
                    }
                    // Non-required new field with no default: silently ignored (no action needed)
                } else {
                    // Field exists in both schema and value records.
                    // Detect when the module author changed the defaultValue AND the child app
                    // still holds the old default (i.e. the user never customised the field).
                    // We detect "user never customised" by comparing value === storedDefaultValue
                    // (the defaultValue hydrated onto the value record at load time from the old schema).
                    const storedDefault = valueRecord.defaultValue;
                    const schemaDefault = field.defaultValue;
                    const currentVal = valueRecord.value;

                    const defaultChanged =
                        schemaDefault !== storedDefault &&
                        schemaDefault != null && schemaDefault !== '' &&
                        currentVal === storedDefault; // user never overrode the old default

                    if (defaultChanged) {
                        changes.push({
                            fieldName,
                            oldValue: storedDefault || '',
                            newValue: schemaDefault,
                            state: "Modified",
                            isBlocker: false,
                        });
                    }
                }
            });
        });

        // Fields whose value records exist on the child app but are no longer in the schema
        currentValues.forEach(valueRecord => {
            if (valueRecord.templateFormFieldId && !schemaFieldIds.has(valueRecord.templateFormFieldId)) {
                if (valueRecord.value && valueRecord.value.trim() !== '') {
                    const fieldName = valueRecord.title || valueRecord.label || 'Untitled Field';
                    changes.push({
                        fieldName,
                        oldValue: valueRecord.value,
                        newValue: '',
                        state: "Removed",
                        isBlocker: false,
                    });
                }
            }
        });

        return changes;
    }

    /**
     * Schema-change diff for multiple applied templates (Spark multi-template variant).
     */
    getSchemaChangesForAllTemplates(appliedTemplates: AppliedApplicationTemplateModel[]): Array<{
        fieldName: string;
        oldValue: string;
        newValue: string;
        state: "Added" | "Modified" | "Removed";
        isBlocker: boolean;
    }> {
        const allChanges: Array<{
            fieldName: string;
            oldValue: string;
            newValue: string;
            state: "Added" | "Modified" | "Removed";
            isBlocker: boolean;
        }> = [];

        (appliedTemplates || []).forEach(template => {
            const changes = this.getSchemaChangesForDisplay(template);
            allChanges.push(...changes);
        });

        return allChanges;
    }

    /**
     * Detect which fields have been modified
     */
    private getModifiedFields(appliedTemplateId: string, currentValues: TemplateFormFieldValueModel[]): TemplateFormFieldValueModel[] {
        const originalValues = this.originalValuesMap.get(appliedTemplateId) || [];
        const modifiedFields: TemplateFormFieldValueModel[] = [];

        currentValues?.forEach(currentField => {
            let originalField = null;

            // Try to find matching original field
            if (currentField.id) {
                originalField = originalValues.find(v => v.id === currentField.id);
            } else if (currentField.templateFormFieldId) {
                originalField = originalValues.find(v =>
                    v.templateFormFieldId === currentField.templateFormFieldId &&
                    v.appliedApplicationTemplateId === currentField.appliedApplicationTemplateId
                );
            } else if (currentField.dynamicFormFieldName) {
                originalField = originalValues.find(v => v.dynamicFormFieldName === currentField.dynamicFormFieldName);
            }

            // If no original or value changed, it's modified
            if (!originalField || originalField.value !== currentField.value) {
                modifiedFields.push(currentField);
            }
        });

        return modifiedFields;
    }

    async updateAppliedTemplate(appliedTemplateId: string, model: UpdateAppliedApplicationTemplateFormRequest) {
        try {
            await this.setSaving(true);
            const result = await voicifyApi.updateAppliedApplicationTemplate(appliedTemplateId, model);
            if (result.resultType == "Ok") {

                const appliedTemplates = this.state.appliedTemplates;
                const existingAppliedTemplate = appliedTemplates.find(m => m.id == appliedTemplateId);
                if (existingAppliedTemplate != null)
                    appliedTemplates[appliedTemplates.indexOf(existingAppliedTemplate)] = result.data;
                else
                    appliedTemplates.push(result.data);

                await this.setState({
                    ...this.state,
                    isSaving: false,
                    appliedTemplates,
                    errors: []
                })
            } else {
                await this.setState({
                    ...this.state,
                    isSaving: false,
                    errors: result.errors
                })
            }
            return result;
        } catch (e) {
            await this.setState({
                ...this.state,
                isSaving: false,
                errors: [e?.toString()]
            })
        }
    }

    async insertDynamicFormSection(appliedTemplateId: string, dynamicFormSection: TemplateFormSectionModel, finishedSection: TemplateFormSectionModel): Promise<TemplateFormSectionModel> {
        // first find applied template
        const appliedTemplates = this.state.appliedTemplates;
        const appliedTemplate = appliedTemplates.find(m => m.id == appliedTemplateId);
        if (!appliedTemplate) return;

        // add dynamic section as next section to fill out
        let sections = [...appliedTemplate.templateForm.templateFormSections];
        dynamicFormSection.priority = finishedSection.priority + .0001;
        sections.push(dynamicFormSection);
        sections = _.sortBy(sections, s => s.priority);

        // find any values in our dynamic values cache that we have for the fields in this dynamic section
        var values = appliedTemplate.templateFormFieldValues;
        var dynamicValuesCache = appliedTemplate.dynamicFormFieldValuesCache;
        dynamicFormSection.dynamicFormFields.forEach(f => {
            var cachedValue = dynamicValuesCache?.find(v => f.name && v.dynamicFormFieldName == f.name);
            if (cachedValue) {
                values.push(cachedValue);
            }
        });

        // update applied template and set state
        appliedTemplates[appliedTemplates.indexOf(appliedTemplate)].templateForm.templateFormSections = sections;
        appliedTemplates[appliedTemplates.indexOf(appliedTemplate)].templateFormFieldValues = values;
        await this.setState({
            ...this.state,
            appliedTemplates
        })
        return dynamicFormSection;
    }

    async removeDynamicFormSection(appliedTemplateId: string, sectionName: string) {
        // first find applied template
        const appliedTemplates = this.state.appliedTemplates;
        const appliedTemplate = appliedTemplates.find(m => m.id == appliedTemplateId);
        if (!appliedTemplate) return;

        // remove the dynamic section
        let sections = [...appliedTemplate.templateForm.templateFormSections];
        let sectionIndex = sections.findIndex(s => s.name == sectionName);
        sections.splice(sectionIndex, 1);

        // update applied template and set state
        appliedTemplates[appliedTemplates.indexOf(appliedTemplate)].templateForm.templateFormSections = sections;
        await this.setState({
            ...this.state,
            appliedTemplates
        })
    }

    finishFormSection(appliedTemplateId: string, model: FinishTemplateSectionRequest): Promise<IResult<DynamicFormResponse>> {
        try {
            const appliedTemplates = this.state.appliedTemplates;
            const appliedTemplate = appliedTemplates.find(m => m.id == appliedTemplateId);
            model.fieldValues = this.stripValuesNotInSections(appliedTemplate, model.fieldValues);
            // if there's no existing applied template something went wrong. 
            if (appliedTemplate == null) {
                this.setState({
                    ...this.state,
                    isSaving: false,
                    errors: ['No applied template loaded']
                })
                return
            }
            appliedTemplates[appliedTemplates.indexOf(appliedTemplate)].templateFormFieldValues = model.fieldValues;
        } catch (e) {
            this.setState({
                ...this.state,
                isSaving: false,
                errors: [e?.toString()]
            })
        }
    }

    /**
     * UNIFIED SAVE METHOD - Consolidates saveForm, finalizeForm, and finalizeForms
     * Handles both intermediate saves and final synchronization
     */
    async saveTemplateFields(
        applicationId: string,
        appliedTemplateId: string,
        currentFieldValues: TemplateFormFieldValueModel[],
        options?: {
            finalize?: boolean,
            publishToEnvironmentId?: string,
            concurrentSyncKey?: string,
            useModifiedFieldsOnly?: boolean,
            /** When true, skip the container's internal waitForSyncToComplete polling loop.
             *  Use this in the Spark client where SparkDeploymentContainer owns polling. */
            skipContainerPolling?: boolean,
        }
    ): Promise<IResult<AppliedApplicationTemplateModel>> {
        this.setSaving(true);

        try {
            const appliedTemplates = this.state.appliedTemplates;
            const appliedTemplate = appliedTemplates.find(m => m.id === appliedTemplateId);

            if (!appliedTemplate) {
                await this.setState({
                    ...this.state,
                    isSaving: false,
                    errors: ['No applied template loaded']
                });
                return { resultType: "Error", errors: ['No applied template loaded'] } as any;
            }

            let fieldValuesToSave = currentFieldValues;

            // If using modified fields only, fetch latest and merge
            if (options?.useModifiedFieldsOnly) {
                const modifiedFields = this.getModifiedFields(appliedTemplateId, currentFieldValues);

                if (modifiedFields.length > 0) {
                    const latestTemplateResult = await voicifyApi.findAppliedApplicationTemplate(appliedTemplateId);

                    if (latestTemplateResult.resultType === "Ok") {
                        fieldValuesToSave = this.mergeModifiedFields(
                            latestTemplateResult.data.templateFormFieldValues,
                            modifiedFields
                        );
                    }
                }
            }

            // Auto-inject defaultValues for fields that exist in the module schema but
            // have no value record on this child app yet (e.g. newly added module fields).
            // This ensures the sync never pushes a blank for a field the module author
            // gave a default value to.
            if (options?.finalize) {
                const sections = appliedTemplate.templateForm?.templateFormSections || [];
                sections.forEach(section => {
                    (section.templateFormFields || []).forEach(field => {
                        if (!field.id) return;
                        // Skip location-specific fields — identified by the sentinel on the value
                        // record: associatedVariable is set AND equals the stored value. Do not inject
                        // a schema default for a field whose existing record marks it location-specific.
                        const existingRecord = fieldValuesToSave.find(v => v.templateFormFieldId === field.id);
                        if (existingRecord?.associatedVariable && existingRecord.associatedVariable === existingRecord.value) return;
                        const hasRecord = !!existingRecord;
                        if (!hasRecord && field.defaultValue != null && field.defaultValue !== '') {
                            fieldValuesToSave = [
                                ...fieldValuesToSave,
                                {
                                    templateFormFieldId: field.id,
                                    appliedApplicationTemplateId: appliedTemplateId,
                                    value: field.defaultValue,
                                    fieldType: field.fieldType,
                                    label: field.label,
                                    title: field.title,
                                    tip: field.tip,
                                    placeholder: field.placeholder,
                                    defaultValue: field.defaultValue,
                                    associatedVariable: field.associatedVariable,
                                } as TemplateFormFieldValueModel,
                            ];
                        }
                    });
                });
            }

            const strippedFieldValues = this.stripValuesNotInSections(appliedTemplate, fieldValuesToSave);
            const updateRequest: UpdateAppliedApplicationTemplateFormRequest = {
                ...appliedTemplate,
                templateFormFieldValues: strippedFieldValues,
                dynamicFormSections: appliedTemplate.templateForm.templateFormSections.filter(s => s?.name),
                publishToEnvironmentId: options?.publishToEnvironmentId
            };

            // If finalize is requested, go directly to sync (Voicify & Spark pattern)
            // The sync endpoint handles both updating and syncing in one call
            if (options?.finalize) {
                const isConcurrentSync = !!options?.concurrentSyncKey;

                await this.setState({
                    ...this.state,
                    appliedTemplates,
                    isWaitingForTemplateSync: true,
                    multipleTemplateSyncsStatus: isConcurrentSync ? "syncing" : "none",
                    templateSyncErrors: [],
                    errors: []
                });

                let syncResult;

                if (appliedTemplate.templateForm.templateConfiguration.finalizeFormUrl) {
                    const request: FinalizeTemplateFormRequest = {
                        applicationId: applicationId,
                        data: updateRequest
                    };
                    syncResult = await voicifyApi.finalizeDynamicForm(appliedTemplateId, request);
                } else {
                    // This endpoint does: PUT UpdateAppliedApplicationTemplateForm (updates + syncs)
                    syncResult = await voicifyApi.queueUpdateApplicationFromTemplateFormsSync(
                        applicationId,
                        appliedTemplateId,
                        updateRequest
                    );
                }

                if (syncResult.resultType === "Ok") {
                    if (syncResult.data.errors && syncResult.data.errors.length > 0) {
                        await this.setState({
                            ...this.state,
                            isSaving: false,
                            isWaitingForTemplateSync: false,
                            multipleTemplateSyncsStatus: isConcurrentSync ? "error" : "none",
                            templateSyncErrors: syncResult.data.errors
                        });
                        return { resultType: "Ok", data: appliedTemplate } as any;
                    } else if (!syncResult.data.appliedTemplateSyncId && !syncResult.data.id) {
                        await this.setState({
                            ...this.state,
                            isSaving: false,
                            isWaitingForTemplateSync: false,
                            multipleTemplateSyncsStatus: isConcurrentSync ? "success" : "none",
                            templateSyncErrors: []
                        });
                        return { resultType: "Ok", data: appliedTemplate } as any;
                    } else {
                        if (options?.skipContainerPolling) {
                            // The caller (e.g. Spark client) owns polling and will track sync
                            // completion via SparkDeploymentContainer. Clear isWaitingForTemplateSync
                            // so the review page's getSubmissionState can fall through to read
                            // the deployment state instead of being permanently stuck on "syncing".
                            await this.setState({
                                ...this.state,
                                appliedTemplateSyncId: syncResult.data.appliedTemplateSyncId || syncResult.data.id,
                                isSaving: false,
                                isWaitingForTemplateSync: false,
                                multipleTemplateSyncsStatus: "none",
                                templateSyncErrors: []
                            });
                        } else {
                            await this.setState({
                                ...this.state,
                                appliedTemplateSyncId: syncResult.data.appliedTemplateSyncId || syncResult.data.id,
                                isSaving: false,
                                templateSyncErrors: []
                            });
                            this.waitForSyncToComplete();
                        }
                        // Return sync result with the sync ID
                        return syncResult;
                    }
                } else {
                    await this.setState({
                        ...this.state,
                        isSaving: false,
                        isWaitingForTemplateSync: false,
                        multipleTemplateSyncsStatus: isConcurrentSync ? "error" : "none",
                        templateSyncErrors: syncResult.errors
                    });
                    return syncResult;
                }
            } else {
                // Not finalizing - just a regular save (intermediate save)
                // This does a PUT to update the template without syncing
                const updateResult = await voicifyApi.updateAppliedApplicationTemplate(appliedTemplateId, updateRequest);

                if (updateResult.resultType !== "Ok") {
                    await this.setState({
                        ...this.state,
                        isSaving: false,
                        errors: updateResult.errors
                    });
                    return updateResult;
                }

                // Update local state
                const index = appliedTemplates.indexOf(appliedTemplate);
                appliedTemplates[index] = updateResult.data;

                // Update original values after successful save
                this.captureOriginalValues(appliedTemplateId, updateResult.data.templateFormFieldValues);

                await this.setState({
                    ...this.state,
                    appliedTemplates,
                    isSaving: false,
                    errors: []
                });

                return updateResult;
            }
        } catch (e) {
            await this.setState({
                ...this.state,
                isSaving: false,
                isWaitingForTemplateSync: false,
                errors: [e?.toString()]
            });
            return { resultType: "Error", errors: [e?.toString()] } as any;
        }
    }

    /**
     * Helper method to merge modified fields with latest values
     */
    private mergeModifiedFields(
        latestValues: TemplateFormFieldValueModel[],
        modifiedFields: TemplateFormFieldValueModel[]
    ): TemplateFormFieldValueModel[] {
        const modifiedFieldsMap = new Map<string, TemplateFormFieldValueModel>();

        modifiedFields.forEach(field => {
            const key = this.getFieldKey(field);
            if (key) {
                modifiedFieldsMap.set(key, field);
            }
        });

        // Start with latest values, replace with modifications
        const merged = latestValues.map(latestField => {
            const key = this.getFieldKey(latestField);
            const modifiedField = modifiedFieldsMap.get(key);
            return modifiedField || latestField;
        });

        // Add any new fields
        modifiedFields.forEach(modifiedField => {
            const key = this.getFieldKey(modifiedField);
            const existsInLatest = latestValues.some(latestField =>
                this.getFieldKey(latestField) === key
            );
            if (!existsInLatest) {
                merged.push(modifiedField);
            }
        });

        return merged;
    }

    /**
     * Generate a unique key for field matching
     */
    private getFieldKey(field: TemplateFormFieldValueModel): string {
        if (field.id) {
            return `id:${field.id}`;
        } else if (field.templateFormFieldId) {
            let key = `templateFormFieldId:${field.templateFormFieldId}`;
            if (field.appliedApplicationTemplateId) {
                key += `:appliedAppId:${field.appliedApplicationTemplateId}`;
            }
            return key;
        } else if (field.dynamicFormFieldName) {
            return `dynamicFormFieldName:${field.dynamicFormFieldName}`;
        }
        return '';
    }

    syncronousUpdateApplicationFromAppliedTemplate(applicationId: string, appliedTemplateId: string, model: UpdateAppliedApplicationTemplateFormRequest) {
        this.setSaving(true);
        const appliedTemplate = this.state.appliedTemplates.find(m => m.id == appliedTemplateId);
        model.dynamicFormSections = appliedTemplate.templateForm.templateFormSections.filter(s => s?.name);
        const promise = voicifyApi.updateApplicationFromTemplateForms(applicationId, appliedTemplateId, model);

        promise.then(result => {
            if (result.resultType == "Ok") {
                this.setState({
                    ...this.state,
                    isSaving: false,
                    errors: []
                })
            }
            else {
                this.setState({
                    ...this.state,
                    isSaving: false,
                    errors: result.errors
                })
            }
        }).catch(e => {
            this.setState({
                ...this.state,
                isSaving: false,
                errors: [e?.toString()]
            })
        })

        return promise;
    }

    stripValuesNotInSections(appliedTemplate: AppliedApplicationTemplateModel, values: TemplateFormFieldValueModel[]): TemplateFormFieldValueModel[] {
        const updatedValues: TemplateFormFieldValueModel[] = [];
        appliedTemplate.templateForm.templateFormSections?.forEach(section => {
            const fields = (section.dynamicFormFields && section.dynamicFormFields.length > 0) ? section.dynamicFormFields : section.templateFormFields;
            fields?.forEach(field => {
                if (field.name) {
                    const value = values.find(v => v.dynamicFormFieldName == field.name);
                    if (value) {
                        updatedValues.push(value);
                    }
                }
                else {
                    const value = values.find(v => v.templateFormFieldId == field.id);
                    if (value) {
                        updatedValues.push(value);
                    }
                }
            })
        });
        return updatedValues;
    }

    async waitForSyncToComplete() {
        try {
            if (!this.state.appliedTemplateSyncId) {
                await this.setState({
                    ...this.state,
                    isWaitingForTemplateSync: false,
                    appliedTemplateSyncId: null,
                    multipleTemplateSyncsStatus: "error",
                    templateSyncErrors: ["We couldn't start syncing your assistant because the server didn't provide a tracking ID for this sync request. Your changes may not have been saved. Please try saving again in a few minutes. If the problem persists, contact Voicify support with the time this occurred."]
                })
                return;
            }

            // Store the sync ID to poll - don't rely on state in the loop
            const syncIdToMonitor = this.state.appliedTemplateSyncId;
            let continuePolling = true;

            // Add timeout to prevent infinite polling (5 minutes max)
            const maxPollingTime = 5 * 60 * 1000; // 5 minutes in milliseconds
            const startTime = Date.now();

            while (continuePolling) {
                // Check if we've exceeded the max polling time
                if (Date.now() - startTime > maxPollingTime) {
                    await this.setState({
                        ...this.state,
                        isWaitingForTemplateSync: false,
                        appliedTemplateSyncId: null,
                        multipleTemplateSyncsStatus: "error",
                        templateSyncErrors: ["Voicify is currently experiencing high sync volume or a temporary technical issue, and your sync request timed out after 5 minutes. Please try again in a few minutes. If the issue continues, reach out to your Voicify contact for support."]
                    });
                    return;
                }

                // Check if sync was cancelled (state was cleared)
                if (!this.state.appliedTemplateSyncId || this.state.appliedTemplateSyncId !== syncIdToMonitor) {
                    // User navigated away or sync was cancelled
                    continuePolling = false;
                    return;
                }

                const result = await voicifyApi.getAppliedTemplateSync(syncIdToMonitor);
                if (result.resultType == "Ok") {
                    const sync = result.data;
                    switch (sync.stage) {
                        case ("Processing"):
                        case ("Queued"):
                            // Still processing, wait and continue polling
                            // Using 5 seconds to balance responsiveness with API load
                            await new Promise(r => setTimeout(r, 5000));
                            break;
                        case ("Complete"):
                            // Sync complete - stop polling
                            continuePolling = false;
                            if (sync.error) {
                                await this.setState({
                                    ...this.state,
                                    isWaitingForTemplateSync: false,
                                    appliedTemplateSyncId: null,
                                    multipleTemplateSyncsStatus: "error",
                                    templateSyncErrors: [sync.error]
                                });
                            } else {
                                await this.setState({
                                    ...this.state,
                                    isWaitingForTemplateSync: false,
                                    appliedTemplateSyncId: null,
                                    multipleTemplateSyncsStatus: "success",
                                    errors: []
                                })
                            }
                            break;
                        default:
                            // Unknown stage - stop polling with error
                            continuePolling = false;
                            await this.setState({
                                ...this.state,
                                isWaitingForTemplateSync: false,
                                appliedTemplateSyncId: null,
                                multipleTemplateSyncsStatus: "error",
                                templateSyncErrors: ["We ran into an unexpected issue while syncing your assistant. Please try again in a few minutes. If the issue continues, reach out to your Voicify contact for support."]
                            });
                    }
                }
                else {
                    // API error - stop polling
                    continuePolling = false;
                    await this.setState({
                        ...this.state,
                        isWaitingForTemplateSync: false,
                        appliedTemplateSyncId: null,
                        multipleTemplateSyncsStatus: "error",
                        templateSyncErrors: result.errors
                    })
                }
            }
        }
        catch (e) {
            await this.setState({
                ...this.state,
                isSaving: false,
                isWaitingForTemplateSync: false,
                multipleTemplateSyncsStatus: "error",
                templateSyncErrors: [e?.toString()]
            })
        }
    }

    async addAppliedTemplateToApp(applicationId: string, templateConfigId: string) {
        try {
            await this.setSaving(true);
            const result = await voicifyApi.addAppliedApplicationTemplate(applicationId, templateConfigId);
            if (result.resultType == "Ok") {
                const appliedTemplates = this.state.appliedTemplates;
                appliedTemplates.push(result.data);
                await this.setState({
                    ...this.state,
                    appliedTemplates,
                    isSaving: false,
                    errors: []
                })
            } else {
                await this.setState({
                    ...this.state,
                    isSaving: false,
                    errors: result.errors
                })
            }
            return result;
        } catch (e) {
            await this.setState({
                ...this.state,
                isSaving: false,
                errors: [e?.toString()]
            })
        }
    }

    async autoGenerateAppliedApplicationTemplateFieldValues(applicationId: string, request: AutoGenerateAppliedAppValuesRequest) {
        try {
            await this.setSaving(true);
            const result = await voicifyApi.autoGenerateAppliedApplicationTemplateFieldValues(applicationId, request);
            if (result.resultType == "Ok") {
                const updatedAppliedTemplates = result.data;
                const errorsList = [];
                for (const updatedAppliedTemplate of updatedAppliedTemplates) {
                    const appliedTemplateToReplaceIndex = this.state.appliedTemplates.findIndex(m => m.id == updatedAppliedTemplate.id);
                    if (appliedTemplateToReplaceIndex > -1) {
                        this.state.appliedTemplates[appliedTemplateToReplaceIndex] = updatedAppliedTemplate;
                    } else {
                        errorsList.push(`Unable to find applied template with id ${updatedAppliedTemplate.id}`);
                    }
                };
                await this.setState({
                    ...this.state,
                    appliedTemplates: this.state.appliedTemplates,
                    isSaving: false,
                    errors: errorsList
                });
            };
            return result;
        } catch (e) {
            this.setState({
                ...this.state,
                isSaving: false,
                errors: [e?.toString()]
            })
        }
    };

    removeAppliedTemplate(applicationId: string, appliedTemplateId: string, successCallback: (result) => void) {
        this.setSaving(true);
        const promise = voicifyApi.removeAppliedApplicationTemplate(applicationId, appliedTemplateId);
        promise.then(result => {
            if (result.resultType == "Ok") {
                this.setState({
                    ...this.state,
                    errors: [],
                    isSaving: false
                }).then(() => successCallback(result))
            }
            else {
                this.setState({
                    ...this.state,
                    errors: result.errors,
                    isSaving: false
                })
            }
        }).catch(e => {
            this.setState({
                ...this.state,
                errors: [e?.toString()],
                isSaving: false
            })
        })
        return promise;
    }

    getLoadedTemplatesForApp(applicationId: string) {
        return this.state.appliedTemplates?.filter(t => t.appliedApplicationId == applicationId) ?? [];
    };

    getCurrentStatus(appliedTemplate: AppliedApplicationTemplateModel) {
        if (!appliedTemplate)
            return "not-enabled";
        const incompleteSections = this.getIncompleteSections(appliedTemplate);
        if (incompleteSections.length > 0) return "incomplete";
        if (appliedTemplate?.lastSyncedDate) return "synced";
        return "complete";
    };

    getIsSectionOfCurrentComplete(appliedTemplate: AppliedApplicationTemplateModel, section: TemplateFormSectionModel) {
        if (!appliedTemplate || !section) {
            return false;
        }

        const currentSection = appliedTemplate?.templateForm?.templateFormSections?.find(s => (section?.name && s?.name == section?.name) || (section?.id && s?.id == section?.id));
        let fields = currentSection?.templateFormFields;
        if (!fields)
            fields = currentSection?.dynamicFormFields;

        const values = appliedTemplate?.templateFormFieldValues;
        if (!fields)
            return false;

        // Collect all fields across all sections so cross-section conditional defaults can be evaluated correctly.
        const allFields = appliedTemplate?.templateForm?.templateFormSections?.flatMap(s => s.templateFormFields ?? s.dynamicFormFields ?? []) ?? [];

        let isComplete = true;
        fields.forEach((field) => {
            if (field?.isRequired) {
                const value = values?.find(v => (field?.id && field.id == v.templateFormFieldId) || (field?.name && field.name == v.dynamicFormFieldName));
                const savedValue = value?.value;
                // A field is incomplete if the saved value is missing/empty AND the conditional default
                // also resolves to nothing. If a conditional default provides a value, treat the field
                // as complete — the default will be persisted on the next save/navigation.
                if (savedValue === null || savedValue === undefined || savedValue === '' || savedValue.trim() === '') {
                    const defaultValue = evaluateConditionalDefault(field, allFields, values ?? []);
                    if (!defaultValue || defaultValue.trim() === '') {
                        isComplete = false;
                    }
                }
            }
        });
        return isComplete;
    };

    getIncompleteSections(appliedTemplate: AppliedApplicationTemplateModel) {
        return appliedTemplate?.templateForm?.templateFormSections?.filter(s => !this.getIsSectionOfCurrentComplete(appliedTemplate, s)) ?? []
    };

    clearErrors() {
        this.setState({
            ...this.state,
            errors: []
        })
    };

    private setLoading(isLoading: boolean) {
        this.setState({
            ...this.state,
            isLoading
        });
    };

    private async setSaving(isSaving: boolean) {
        await this.setState({
            ...this.state,
            isSaving
        });
    };

}